/* ===============================================================
   CONFIGURATOR BUDGET BRIDGE
   assets/configurator-budget-bridge.js

   Shared data layer between configurators and the budget app.
   Single source of truth for:
     • Central BoQ catalog (names, categories, units, dimensions,
       translations) — loaded once from the cloud function shared
       with budget-prd-cat-selector.liquid
     • Per-customer affiliate product assignments (URL, title,
       image, vendor, price) — read & write via window.user_prices
       and the existing window.saveAffiliateProductBasics()
     • Publishing configurator-computed quantities back to the
       budget app via window.ConfiguratorBoQ.variantData

   This file does NOT duplicate logic that budget-prd-cat-selector
   already implements. It calls existing globals when present and
   only falls back to direct fetch when budget isn't loaded yet.

   Architecture (decided with Marek):
     1. Configurator runtime is the canonical source of project AMOUNTS.
     2. Central BoQ Google Sheets is the canonical source of METADATA
        (names, units, translations across 20 locales).
     3. Per-customer assignments live in Firestore via existing
        budget-prd-cat-selector save functions; we DO NOT add a
        parallel persistence path.

   Public API on window.ConfiguratorBudgetBridge:
     init()                        — kick off central BoQ load if needed
     getBoqItem(boqCode)           — returns localized item or null
     getUserPrice(boqCode)         — returns assigned product + price
     saveAffiliateProduct(boqCode, fields)
                                    — writes through budget app's
                                      saveAffiliateProductBasics()
     publishVariantData(variantData)
                                    — emits ConfiguratorBoQ.variantData
                                      so budget knows configurator owns
                                      the BoQ
     onReady(callback)             — fires when central BoQ is loaded
     onPricesUpdated(callback)     — fires when user_prices changes
   =============================================================== */

(function (global) {
  'use strict';

  var VERSION = 'budget-bridge-v10';

  /* ---------------------------------------------------------------
     CENTRAL BoQ — Google Sheets cloud function
     --------------------------------------------------------------- */
  var SHEET_ID  = '1DhpFNi8Nu-l-4p6RvNRNaNqeUE1pbBWLpxefIc-M_mk';
  var SHEET_GID = '638861442';
  /* Served by the leva-scrape worker straight from the native Google Sheet
     (the old Cloud Run function only read the former .xlsx copy). */
  var SHEET_URL = 'https://leva-scrape.long-cloud-d006.workers.dev/sheet' +
                  '?sheetId=' + SHEET_ID + '&gid=' + SHEET_GID;

  /* Column index map for the central BoQ sheet. Maps to the 0-based
     position in each row array. Verified against the sheet structure
     captured in this configurator session.

     If columns are added/reordered in the sheet, ONLY this map needs
     updating — everything else flows from here. */
  /* Column index map for the central BoQ sheet. Matches the layout
     budget-prd-cat-selector.liquid uses verbatim. 0-indexed.

     Verified against budget app's `decoratedTitle()`:
       row[3]  = NAME EN (master)
       row[17] = NOTE METRIC   ← used as π substitution in metric mode
       row[18] = NOTE IMPERIAL ← used as π substitution in imperial mode
       row[19] = DESCRIPTION EN
     and against `languageStartsWith[CS] = 29 → row[29 + 40] = row[69]`
     for the localized CS NAME column.

     If columns are added/reordered in the sheet, ONLY this map and
     LANGUAGE_START_COLS below need updating. */
  var COLS = {
    id:              0,    /* A */
    category_en:     1,    /* B */
    subcategory_en:  2,    /* C */
    name_en:         3,    /* D */
    dim_metric_a:    4,    /* E */
    dim_metric_b:    5,    /* F */
    dim_metric_c:    6,    /* G */
    dim_imperial_a:  7,    /* H */
    dim_imperial_b:  8,    /* I */
    dim_imperial_c:  9,    /* J */
    amount_metric:  10,    /* K — unused in configurator mode (we own amounts) */
    amount_imperial: 11,   /* L */
    unit_metric:    12,    /* M */
    unit_imperial:  13,    /* N */
    note1:          14,    /* O */
    note2:          15,    /* P */
    order:          16,    /* Q */
    note_metric:    17,    /* R ← π metric substitution */
    note_imperial:  18,    /* S ← π imperial substitution */
    description_en: 19,    /* T */
    sample_metric:  20,    /* U */
    sample_imperial:21,    /* V */
    link:           22,    /* W */
    minutes:        23,    /* X */
    youtube:        24,    /* Y */
    additional:     25,    /* Z */
    workers:        26,    /* AA */
    color:          27     /* AB */
    /* Translations: see LANGUAGE_START_COLS. The four blocks are
       CATEGORY → row[langCol +  0]    (cols AC..AV)
       SUBCATEGORY → row[langCol + 20] (cols AW..BP)
       NAME → row[langCol + 40]        (cols BQ..CJ)
       DESCRIPTION → row[langCol + 60] (cols CK..DD) */
  };

  /* Locale → absolute start column for translation lookups. Matches
     budget-prd-cat-selector's `languageStartsWith` exactly so the two
     apps never drift. CS = 29 means the CATEGORY column for Czech is
     at row[29], NAME for Czech at row[29 + 40] = row[69]. */
  var LANGUAGE_START_COLS = {
    en:      0,    /* English uses master columns 1, 2, 3, 19 (no offset) */
    ar:     28,
    cs:     29,
    de:     30,
    el:     31,
    es:     32,
    fi:     33,
    fr:     34,
    he:     35,
    hu:     36,
    it:     37,
    ja:     38,
    ko:     39,
    nl:     40,
    no:     41,
    pl:     42,
    'pt-pt':43,
    pt:     43,
    ro:     44,
    sv:     45,
    tr:     46,
    'zh-cn':47,
    zh:     47
  };

  var TRANSLATION_OFFSET = {
    category:    0,
    subcategory: 20,
    name:        40,
    description: 60
  };

  /* ---------------------------------------------------------------
     STATE
     --------------------------------------------------------------- */
  var _rows = null;             /* [[col0, col1, ...], ...] from cloud fn */
  var _byId = null;             /* { 'KVH10060': row, ... } */
  var _loadPromise = null;
  var _readyCallbacks = [];
  var _pricesUpdatedCallbacks = [];

  /* Locale resolution mirrors budget-prd-cat-selector: prefer
     `Shopify.locale` (which Shopify sets to the active store language
     like 'en', 'cs', 'pt-PT'), fall back to <html lang>. Normalize to
     lowercase for our LANGUAGE_START_COLS lookups. */
  var _locale = (function () {
    var src = (global.Shopify && global.Shopify.locale) ||
              document.documentElement.lang ||
              'en';
    var l = String(src).toLowerCase();
    if (LANGUAGE_START_COLS[l] != null) return l;
    /* Try the short form (e.g. 'pt' from 'pt-pt'). */
    if (l.indexOf('-') >= 0) {
      var short = l.split('-')[0];
      if (LANGUAGE_START_COLS[short] != null) return short;
    }
    return 'en';
  })();

  function _log(msg) {
    if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
      global.Configurator.log.log('[budget-bridge] ' + msg);
    }
  }

  /* ---------------------------------------------------------------
     CENTRAL BoQ — load + parse
     --------------------------------------------------------------- */

  /* Strategy: prefer budget-prd-cat-selector's cache if it has already
     loaded the same sheet (saves a network round-trip and guarantees
     identical data). Falls back to direct fetch otherwise. */
  function _hasBudgetCache() {
    /* budget-prd-cat-selector exposes the parsed map at
       window.centralProductMap once loadCSV() has run. We can
       reverse-engineer rows from it but it's keyed differently —
       safer to just do our own fetch (cache TTL is 5 min anyway,
       so the cost is one shared fetch per session). */
    return Array.isArray(global._configuratorBoqRows) &&
           global._configuratorBoqRows.length > 0;
  }

  function loadCentralBoq() {
    if (_loadPromise) return _loadPromise;

    if (_hasBudgetCache()) {
      _rows = global._configuratorBoqRows;
      _buildIndex();
      _loadPromise = Promise.resolve(_rows);
      _fireReady();
      return _loadPromise;
    }

    _log('fetching central BoQ from cloud function');
    _loadPromise = fetch(SHEET_URL, {
      method: 'GET',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(function (resp) { return resp.text(); })
      .then(function (text) {
        var parsed;
        try { parsed = JSON.parse(text); }
        catch (err) {
          _log('failed to parse central BoQ JSON: ' + err.message);
          throw err;
        }
        _rows = (parsed && parsed.data) || [];
        global._configuratorBoqRows = _rows;   /* cache for other configs */
        _buildIndex();
        _log('loaded ' + _rows.length + ' rows (locale=' + _locale + ')');
        _fireReady();
        return _rows;
      })
      .catch(function (err) {
        _log('load failed: ' + (err && err.message ? err.message : err));
        _loadPromise = null;    /* allow retry */
        throw err;
      });

    return _loadPromise;
  }

  function _buildIndex() {
    _byId = {};
    /* Rows 0 and 1 are headers per the sheet structure (header row +
       sub-header row with A/B/C dimension labels). Real data starts
       at index 2. We skip any row whose first cell isn't a non-empty
       string code, so the index is robust if the sheet adds blank
       spacer rows. */
    for (var i = 2; i < _rows.length; i++) {
      var row = _rows[i];
      if (!row || !row[COLS.id]) continue;
      var id = String(row[COLS.id]).trim();
      if (id) _byId[id] = row;
    }
  }

  function _fireReady() {
    var cbs = _readyCallbacks.slice();
    _readyCallbacks = [];
    cbs.forEach(function (cb) {
      try { cb(); } catch (err) {
        _log('onReady callback threw: ' + (err && err.message ? err.message : err));
      }
    });
  }

  /* ---------------------------------------------------------------
     LOCALIZATION HELPERS
     --------------------------------------------------------------- */

  /* Returns a localized field for the active locale.
     Matches budget-prd-cat-selector's `decoratedTitle()` pattern:

       const languageIndex = languageStartsWith[language.toUpperCase()];
       const title = language !== 'en'
         ? row[languageIndex + 40]    // localized NAME
         : row[3];                    // master EN NAME

     For us: kind = 'name' → offset 40, 'category' → 0, etc.
     enFallbackCol = the master English column for that field. */
  function _localizedField(row, kind, enFallbackCol) {
    if (!row) return null;
    /* English: read the master column directly. */
    if (_locale === 'en') return row[enFallbackCol];
    var langStart = LANGUAGE_START_COLS[_locale];
    var offset    = TRANSLATION_OFFSET[kind];
    if (typeof langStart !== 'number' || typeof offset !== 'number') {
      return row[enFallbackCol];
    }
    var v = row[langStart + offset];
    /* Empty translation cell → fall back to master EN so the user
       never sees a blank. */
    if (v == null || String(v).trim() === '') return row[enFallbackCol];
    return v;
  }

  /* π substitution — mirrors budget-prd-cat-selector's decoratedTitle().
     Names in the central BoQ contain "π" as a placeholder for the
     size descriptor that varies between metric and imperial:

       "EXTERIOR DOOR π LH"   + row[17]="800"   → "EXTERIOR DOOR 800 LH"
       "EXTERIOR DOOR π LH"   + row[18]="30In." → "EXTERIOR DOOR 30In. LH"

     This is HOW the same row serves both unit systems without
     duplicating translations across 20 languages. The configurator's
     STATE.isMetric flips which note column we substitute from. */
  function _applyPiSubstitution(text, row) {
    if (!text || text.indexOf('π') === -1) return text;
    var isMetric = !global.STATE || global.STATE.isMetric !== false;
    var col = isMetric ? COLS.note_metric : COLS.note_imperial;
    var sub = row[col];
    if (sub == null) return text;
    return String(text).replace(/π/g, String(sub));
  }

  /* ---------------------------------------------------------------
     PUBLIC: getBoqItem
     Returns a localized BoQ item by code, with π size placeholders
     substituted based on the current unit system (metric/imperial).
     --------------------------------------------------------------- */
  function getBoqItem(boqCode) {
    if (!_byId || !boqCode) return null;
    var row = _byId[boqCode];
    if (!row) return null;

    var rawName = _localizedField(row, 'name', COLS.name_en);
    var rawDesc = _localizedField(row, 'description', COLS.description_en);

    return {
      id:                 row[COLS.id],
      name:               _applyPiSubstitution(rawName, row),
      category:           _localizedField(row, 'category',    COLS.category_en),
      subcategory:        _localizedField(row, 'subcategory', COLS.subcategory_en),
      description:        _applyPiSubstitution(rawDesc, row),
      unitMetric:         row[COLS.unit_metric],
      unitImperial:       row[COLS.unit_imperial],
      dimensionsMetric:   {
        a: row[COLS.dim_metric_a],
        b: row[COLS.dim_metric_b],
        c: row[COLS.dim_metric_c]
      },
      dimensionsImperial: {
        a: row[COLS.dim_imperial_a],
        b: row[COLS.dim_imperial_b],
        c: row[COLS.dim_imperial_c]
      },
      noteMetric:         row[COLS.note_metric],
      noteImperial:       row[COLS.note_imperial],
      order:              row[COLS.order],
      link:               row[COLS.link],
      minutes:            row[COLS.minutes],
      workers:            row[COLS.workers]
    };
  }

  /* ---------------------------------------------------------------
     UNIT CONVERSION — storage ↔ display
     ---------------------------------------------------------------
     Budget storage convention (per saveAffiliateProductBasics):
       Unit price is always stored as USD per METRIC unit, regardless
       of the unit the user typed it in. So $7 per FT entered in
       imperial mode is stored as $22.97 per M (× 3.281).
       At display time we MUST convert it back, otherwise the drawer
       shows the metric-storage value to an imperial user and they see
       a ~3.28× inflated price.

     This mirrors budget's convertMetricUnitPriceToCurrentUnit. */
  var UNIT_CONVERSION_COEFFICIENTS = {
    'M':   3.281,
    'M2':  10.764,
    'M3':  35.315,
    'PCS': 1,
    'KG':  2.205
  };

  /* The catalog decides the imperial unit, so the factor follows the
     metric > imperial PAIR of the item, not the metric unit alone:
     volumes are M3>YD3 (concrete, gravel, soil), M3>FT3 only for
     insulation. Same table as the budget page (budget-prd-cat-selector). */
  var UNIT_PAIR_FACTORS = {
    'M>FT':    3.28084,
    'M2>FT2':  10.7639,
    'M3>YD3':  1.30795,
    'M3>FT3':  35.3147,
    'KG>LB':   2.20462,
    'L>GAL':   0.264172
  };

  function _pairFactor(item) {
    if (!item) return null;
    var m = String(item.unitMetric || '').trim().toUpperCase();
    var i = String(item.unitImperial || '').trim().toUpperCase();
    if (!m) return null;
    if (m === i) return 1;
    var f = UNIT_PAIR_FACTORS[m + '>' + i];
    if (f) return f;
    return UNIT_CONVERSION_COEFFICIENTS[m] || null;
  }

  /* PUBLIC: metric quantity x unitFactor(code) = quantity in the item's
     imperial catalog unit. null when the item is unknown. */
  function unitFactor(boqCode) {
    return _pairFactor(getBoqItem(boqCode));
  }

  function _convertStoredPriceToCurrentUnit(metricPrice, boqCode) {
    if (!isFinite(metricPrice)) return metricPrice;
    /* When configurator is in metric mode, storage IS the display value. */
    var isMetric = !global.STATE || global.STATE.isMetric !== false;
    if (isMetric) return metricPrice;

    var item = getBoqItem(boqCode);
    if (!item) return metricPrice;
    var coef  = _pairFactor(item);
    if (coef) return metricPrice / coef;
    /* Unknown unit — fall back to qty ratio (matches budget's safety net). */
    var byMat = (global.ConfiguratorBoQ && global.ConfiguratorBoQ.byMaterial) || {};
    var metricQty = byMat[boqCode];
    if (!metricQty || metricQty <= 0) return metricPrice;
    var amount = _getConfiguratorAmount(boqCode);
    if (amount.imperial > 0) return (metricPrice * metricQty) / amount.imperial;
    return metricPrice;
  }

  /* ---------------------------------------------------------------
     PUBLIC: getUserPrice
     Reads a per-customer assignment for one BoQ code from
     window.user_prices (populated by budget-prd-cat-selector at
     page load). Returns null if no product has been assigned yet.

     The returned `price` is ALREADY converted to the configurator's
     current unit system, so the drawer can display it directly
     without further math. The original metric-storage value is also
     exposed as `priceMetric` for debugging.
     --------------------------------------------------------------- */
  /* Read the stored unit price in the currently active Shopify
     currency. Mirrors budget.liquid's retrievePriceLocal exactly:

       • USD shop  → user_prices[code]   (master USD value)
       • Other     → user_prices[code_CURRENCY]  if present (no rate drift),
                     else USD master × Shopify.currency.rate
     This is at the metric-storage scale ($/M); the caller still
     needs to convert to the current unit system for display. */
  function _getStoredMetricPrice(boqCode) {
    var prices = global.user_prices || {};
    var activeCurrency = _detectCurrency();
    if (activeCurrency.toUpperCase() === 'USD') {
      var v = parseFloat(prices[boqCode]);
      return isFinite(v) ? v : null;
    }
    var local = parseFloat(prices[boqCode + '_' + activeCurrency]);
    if (isFinite(local)) return local;
    var usd = parseFloat(prices[boqCode]);
    if (!isFinite(usd)) return null;
    var rate = (global.Shopify && global.Shopify.currency && Number(global.Shopify.currency.rate)) || 1;
    return usd * (rate || 1);
  }

  function getUserPrice(boqCode) {
    var prices = global.user_prices || {};
    if (!boqCode) return null;
    var hasAnything =
      prices[boqCode + '_affiliate_product_url']       != null ||
      prices[boqCode + '_affiliate_product_title']     != null ||
      prices[boqCode + '_affiliate_product_image_url'] != null ||
      prices[boqCode]                                  != null;
    if (!hasAnything) return null;

    /* Active shop currency (never the stored _currentCurrency) — user
       might have switched languages / currencies since the save. */
    var currency = _detectCurrency();

    var metricPrice = _getStoredMetricPrice(boqCode);
    var displayPrice = isFinite(metricPrice)
      ? _convertStoredPriceToCurrentUnit(metricPrice, boqCode)
      : null;

    return {
      url:         prices[boqCode + '_affiliate_product_url']       || null,
      title:       prices[boqCode + '_affiliate_product_title']     || null,
      image:       prices[boqCode + '_affiliate_product_image_url'] || null,
      source:      prices[boqCode + '_affiliate_product_source']    || null,
      price:       displayPrice,
      priceMetric: isFinite(metricPrice) ? metricPrice : null,
      currency:    currency
    };
  }

  function _detectCurrency() {
    if (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) {
      return global.Shopify.currency.active;
    }
    return 'USD';
  }

  /* ---------------------------------------------------------------
     PUBLIC: isCurrentVariantPurchased
     Mirrors budget.liquid's gating check. Returns true when the
     current project variant has been purchased (server-side rendered
     into window.__budgetPurchasedVariantIds). Drawer uses this to
     apply a faded overlay for trial users.

     CRITICAL: this must read the active variant from the SAME source
     as budget.liquid's getActiveVariantId(), otherwise we'll get a
     mismatch — budget says purchased, bridge says not. Budget reads:

       document.querySelector('#variant-buttons .variant-tab[data-active="true"]')
                .getAttribute('data-variant-id')

     We try that first, then fall back to budget.liquid alternates
     (#variantId input, window.__activeVariantId) so configurators
     that don't render variant-buttons still work.
     --------------------------------------------------------------- */
  function _getActiveVariantId() {
    /* PRIORITY: the configurator owns the active variant. When the user
       switches variant inside the configurator, syncShopifyVariant()
       updates the cart form's hidden [name="id"] and the URL ?variant=,
       but budget.liquid's #variant-buttons does NOT change. So we must
       read the configurator-driven sources FIRST, otherwise we'd keep
       returning the stale variant the budget tabs still point at. */

    /* Source 1: cart form hidden id — kept in sync by syncShopifyVariant. */
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      var idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) {
        var vc = parseInt(idInput.value, 10);
        if (!isNaN(vc)) return vc;
      }
    }
    /* Source 2: URL ?variant= — also kept in sync by syncShopifyVariant. */
    try {
      var urlV = new URLSearchParams(global.location.search).get('variant');
      if (urlV) {
        var vu = parseInt(urlV, 10);
        if (!isNaN(vu)) return vu;
      }
    } catch (e) {}

    /* Source 3: the variant tab budget.liquid renders (only correct when
       the budget tabs ARE the variant control, e.g. non-configurator). */
    var activeBtn = document.querySelector('#variant-buttons .variant-tab[data-active="true"]');
    if (activeBtn) {
      var v = parseInt(activeBtn.getAttribute('data-variant-id'), 10);
      if (!isNaN(v)) return v;
    }
    /* Fallback 1: input element (some legacy templates). */
    var el = document.getElementById('variantId');
    if (el && el.value) {
      var v2 = parseInt(el.value, 10);
      if (!isNaN(v2)) return v2;
    }
    /* Fallback 2: explicit global. */
    var v3 = parseInt(global.__activeVariantId, 10);
    if (!isNaN(v3)) return v3;
    /* Fallback 3: single-variant products — the configurator's RK_BOOT
       (or equivalent) is the canonical source. */
    if (global.RK_BOOT && global.RK_BOOT.variantId) {
      var v4 = parseInt(global.RK_BOOT.variantId, 10);
      if (!isNaN(v4)) return v4;
    }
    return null;
  }

  function isCurrentVariantPurchased() {
    var ids = global.__budgetPurchasedVariantIds;
    if (!Array.isArray(ids) || !ids.length) return false;
    var variantId = _getActiveVariantId();
    if (variantId == null) return false;
    return ids.indexOf(variantId) !== -1;
  }

  /* ---------------------------------------------------------------
     PUBLIC: saveAffiliateProduct
     Writes through the budget app's existing save function so that
     all writes go through the same Firestore path. Falls back to a
     local-only write to window.user_prices if budget isn't loaded,
     so the configurator drawer stays usable while offline (changes
     just won't persist until budget app boots).

     fields: { url, title, image, source, price }
     --------------------------------------------------------------- */
  function saveAffiliateProduct(boqCode, fields) {
    if (!boqCode) return Promise.reject(new Error('saveAffiliateProduct: boqCode required'));
    fields = fields || {};

    /* Preferred path: the budget app's own save function. It handles
       Firestore writes, USD conversion, currency normalization, and
       UI re-renders inside the budget. We pass through verbatim. */
    if (typeof global.saveAffiliateProductBasics === 'function') {
      /* saveAffiliateProductBasics needs window.customDrawerMetaData
         to be set so it can read currentUnit + metricUnits +
         imperialUnits. The configurator drawer doesn't have access
         to those (configurator computes amount, not the meta sheet).
         Solution: stash a minimal-shape meta for the duration of the
         call. */
      var prevMeta = global.customDrawerMetaData;
      var bridgeMeta = _buildBridgeMeta(boqCode);
      global.customDrawerMetaData = bridgeMeta;

      try {
        global.saveAffiliateProductBasics(
          boqCode,
          fields.url    || '',
          fields.title  || '',
          fields.image  || '',
          fields.source || '',
          fields.price != null ? fields.price : 0
        );
      } finally {
        /* Restore previous meta synchronously; the save is async but
           saveAffiliateProductBasics captures meta at call time. */
        global.customDrawerMetaData = prevMeta;
      }
      /* saveAffiliateProductBasics doesn't return a promise — we
         poll user_prices for the update before firing onPricesUpdated. */
      _pollForPriceUpdate(boqCode, fields.url);
      return Promise.resolve();
    }

    /* Fallback: write directly to user_prices in memory. Better than
       nothing if budget app isn't loaded; user can re-save via the
       budget drawer later to persist. */
    _log('saveAffiliateProductBasics not available, in-memory only');
    if (!global.user_prices) global.user_prices = {};
    global.user_prices[boqCode + '_affiliate_product_url']       = fields.url    || '';
    global.user_prices[boqCode + '_affiliate_product_title']     = fields.title  || '';
    global.user_prices[boqCode + '_affiliate_product_image_url'] = fields.image  || '';
    global.user_prices[boqCode + '_affiliate_product_source']    = fields.source || '';
    if (fields.price != null) global.user_prices[boqCode] = String(fields.price);
    _firePricesUpdated(boqCode);
    return Promise.resolve();
  }

  /* Build a synthetic customDrawerMetaData using the central BoQ
     item's units and the configurator's project amount.

     CRITICAL: currentUnitSystem must match STATE.isMetric so that
     saveAffiliateProductBasics interprets the entered price in the
     RIGHT unit. If the configurator is in imperial mode and we tell
     budget "metric", it back-converts the price wrongly and stores
     a 3.28× inflated USD-per-meter value. */
  function _buildBridgeMeta(boqCode) {
    var item = getBoqItem(boqCode) || {};
    var amount = _getConfiguratorAmount(boqCode);
    var isMetric = !global.STATE || global.STATE.isMetric !== false;

    /* Build the selectedProduct shape that saveAffiliateProductBasics
       indexes into (it reads [10] = metric qty, [11] = imperial qty,
       [12] = metric unit label, [13] = imperial unit label). We
       construct a sparse array that satisfies just those positions. */
    var selectedProduct = [];
    selectedProduct[0]  = boqCode;
    selectedProduct[10] = amount.metric  != null ? String(amount.metric)   : '1';
    selectedProduct[11] = amount.imperial != null ? String(amount.imperial) : '1';
    selectedProduct[12] = item.unitMetric   || 'PCS';
    selectedProduct[13] = item.unitImperial || 'PCS';
    return {
      inputId:           boqCode,
      selectedProduct:   selectedProduct,
      metricQuantity:    amount.metric,
      imperialQuantity:  amount.imperial,
      currentUnitSystem: isMetric ? 'metric' : 'imperial'
    };
  }

  function _getConfiguratorAmount(boqCode) {
    /* Configurator's RackBoQ.compute publishes quantities ALWAYS in
       metric base units:
         linear items (KVH lumber, decking, batten)    → meters
         area items   (corrugated roofing)             → m²
         piece items  (fasteners, hardware)            → integers
       Budget app reads selectedProduct[10] = metric qty,
                          selectedProduct[11] = imperial qty.
       saveAffiliateProductBasics picks ONE of them based on the
       current unit system and uses it to back-convert the entered
       price to a USD-per-metric-unit storage value.

       If we naively put `qty` into both slots, imperial-mode users
       would end up with the metric number under the imperial label,
       making the back-conversion ratio wrong by a factor of ~3.28
       (linear) or ~10.76 (area). That's exactly the symptom Marek
       saw: $7 × 151.93 FT = $1063, but the displayed line shows
       $7 × 46.32 m = $324 — a ~3× discrepancy.

       The right conversion depends on the BoQ item's metric UNIT
       string ('m', 'm²', 'PCS' / 'M', 'M2', 'PCS'). For PCS, metric
       and imperial are identical. */
    var byMat = (global.ConfiguratorBoQ && global.ConfiguratorBoQ.byMaterial) || {};
    var qty = byMat[boqCode];
    if (qty == null) return { metric: 1, imperial: 1 };

    var item = getBoqItem(boqCode);
    var unit = item ? String(item.unitMetric || '').trim().toLowerCase() : '';
    var metric = qty;
    var imperial;
    if (unit === 'm' || unit === 'meter' || unit === 'meters') {
      imperial = qty * 3.2808;       /* meters → feet */
    } else if (unit === 'm²' || unit === 'm2' || unit === 'sqm') {
      imperial = qty * 10.7639;      /* m² → ft² */
    } else {
      imperial = qty;                /* PCS / count — identical */
    }
    return { metric: metric, imperial: imperial };
  }

  function _pollForPriceUpdate(boqCode, expectedUrl) {
    /* saveAffiliateProductBasics writes to user_prices inside a .then()
       after Firestore commit. We poll briefly, then fire the event.
       Cap at 10 attempts (1 s) — Firestore is normally <300 ms. */
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      var current = (global.user_prices || {})[boqCode + '_affiliate_product_url'];
      if (current === expectedUrl || attempts >= 10) {
        clearInterval(timer);
        _firePricesUpdated(boqCode);
      }
    }, 100);
  }

  /* ---------------------------------------------------------------
     PUBLIC: publishVariantData
     Configurator calls this whenever its BoQ recomputes. Budget app
     checks window.ConfiguratorBoQ.variantData and prefers it over
     the per-project Google Sheet (CONFIGURATOR-OWNED MODE in
     budget-prd-cat-selector).

     Input: byMaterial = { 'KVH10060': 18.73, 'TST00001': 4, ... }
     Output: variantData = [
       [], [],                                  // 2 spacer rows
       ['KVH10060', '18.73', '61.4'],           // [id, metricQty, imperialQty]
       ['TST00001', '4',     '13.12'],
       ...
     ]
     --------------------------------------------------------------- */
  function publishVariantData(byMaterial) {
    if (!byMaterial || typeof byMaterial !== 'object') return;
    /* The shape expected by budget-prd-cat-selector's loadVariantCSV
       result handler is: [[header1], [header2], [id, metric, imperial], ...].
       Indices 0 and 1 are reserved for headers — we leave them empty. */
    var variantData = [[], []];
    Object.keys(byMaterial).forEach(function (id) {
      var qty = byMaterial[id];
      if (qty == null || qty === 0) return;
      var metric = String(qty);
      /* For PCS items (non-linear), imperial == metric. For linear/m²
         items, budget app handles the conversion itself based on the
         central BoQ unit label. We pass metric here; budget converts. */
      var imperial = String(qty);
      variantData.push([id, metric, imperial]);
    });

    global.ConfiguratorBoQ = global.ConfiguratorBoQ || {};
    global.ConfiguratorBoQ.variantData = variantData;
    global.ConfiguratorBoQ.byMaterial  = byMaterial;

    /* Notify budget app. budget-prd-cat-selector listens for the
       'configurator:boq-updated' event in CONFIGURATOR-OWNED mode. */
    try {
      global.dispatchEvent(new CustomEvent('configurator:boq-updated', {
        detail: { byMaterial: byMaterial, variantData: variantData }
      }));
    } catch (e) { /* IE/old browser — silently fail */ }
  }

  /* ---------------------------------------------------------------
     EVENTS
     --------------------------------------------------------------- */
  function onReady(callback) {
    if (typeof callback !== 'function') return;
    if (_byId) {
      try { callback(); } catch (err) { _log('onReady callback threw'); }
    } else {
      _readyCallbacks.push(callback);
    }
  }

  function onPricesUpdated(callback) {
    if (typeof callback === 'function') _pricesUpdatedCallbacks.push(callback);
  }

  function _firePricesUpdated(boqCode) {
    _pricesUpdatedCallbacks.forEach(function (cb) {
      try { cb(boqCode); } catch (err) {
        _log('onPricesUpdated callback threw');
      }
    });
    try {
      global.dispatchEvent(new CustomEvent('configurator:user-prices-updated', {
        detail: { boqCode: boqCode }
      }));
    } catch (e) {}
  }

  /* ---------------------------------------------------------------
     INIT
     --------------------------------------------------------------- */
  function init() {
    loadCentralBoq().catch(function () {
      /* swallowed — _log already recorded the failure */
    });
    _watchVariantChanges();
  }

  /* When the user clicks a different variant tab in budget.liquid,
     the data-active attribute flips. The drawer's lock state, BoQ
     totals, and user_prices may all be variant-specific, so we
     dispatch the same event the bridge uses for price changes,
     prompting subscribed components (the drawer) to re-render.

     We watch the #variant-buttons container for attribute changes
     instead of binding click handlers — that way we catch programmatic
     changes from anywhere (URL routing, A/B tests, etc.) too. */
  var _variantObserver = null;
  function _watchVariantChanges() {
    /* The configurator (bench/rack/pool/…) fires this on its OWN
       variant switch (selectVariant). budget.liquid's #variant-buttons
       don't change in that case, so without this listener the drawer
       lock + banner masking kept showing the previous variant's
       purchase state. Re-fire prices-updated so every subscriber
       re-evaluates isCurrentVariantPurchased() for the new variant. */
    if (!_configuratorVariantBound) {
      _configuratorVariantBound = true;
      global.addEventListener('configurator:variant-changed', function () {
        _log('configurator variant changed → firing prices-updated');
        _firePricesUpdated(null);
      });
    }

    if (_variantObserver) return;       /* idempotent */
    var container = document.getElementById('variant-buttons');
    if (!container) {
      /* budget.liquid might not be loaded yet — try again after a
         short delay. Cap retries so we don't spin forever on pages
         that genuinely don't have variant tabs. */
      if (!_variantRetryCount) _variantRetryCount = 0;
      if (_variantRetryCount < 20) {
        _variantRetryCount++;
        setTimeout(_watchVariantChanges, 250);
      }
      return;
    }
    _variantObserver = new MutationObserver(function () {
      _log('active variant changed → firing prices-updated');
      _firePricesUpdated(null);
    });
    _variantObserver.observe(container, {
      attributes: true,
      attributeFilter: ['data-active'],
      subtree: true
    });
  }
  var _variantRetryCount = 0;
  var _configuratorVariantBound = false;

  global.ConfiguratorBudgetBridge = {
    __version:           VERSION,
    init:                init,
    loadCentralBoq:      loadCentralBoq,
    getBoqItem:          getBoqItem,
    unitFactor:          unitFactor,
    getUserPrice:        getUserPrice,
    /* True once the central BoQ rows have been loaded (i.e. getBoqItem
       will return real names instead of nulls). PDF export uses this
       to gate the workflow — exporting before names are ready would
       produce a PDF where every row shows the BoQ ID instead of the
       human label. */
    isReady:             function () { return !!_byId; },
    isCurrentVariantPurchased: isCurrentVariantPurchased,
    saveAffiliateProduct: saveAffiliateProduct,
    publishVariantData:  publishVariantData,
    onReady:             onReady,
    onPricesUpdated:     onPricesUpdated,
    /* Diagnostic: expose internals so admin can inspect from the console. */
    _debug: {
      get rows()   { return _rows; },
      get byId()   { return _byId; },
      get locale() { return _locale; },
      /* Quick health check Marek can run in console:
         window.ConfiguratorBudgetBridge._debug.purchaseStatus() */
      purchaseStatus: function () {
        var ids = global.__budgetPurchasedVariantIds;
        var activeId = _getActiveVariantId();
        return {
          purchasedIds:     ids,
          purchasedIdsType: Array.isArray(ids) ? 'array(' + ids.length + ')' : typeof ids,
          activeVariantId:  activeId,
          activeVariantSrc: (function () {
            var form = document.querySelector('form[action*="/cart/add"]');
            if (form && form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value) return 'cart form [name=id]';
            try { if (new URLSearchParams(global.location.search).get('variant')) return 'URL ?variant='; } catch (e) {}
            if (document.querySelector('#variant-buttons .variant-tab[data-active="true"]')) return '#variant-buttons';
            if (document.getElementById('variantId')) return '#variantId input';
            if (global.__activeVariantId != null) return 'window.__activeVariantId';
            if (global.RK_BOOT && global.RK_BOOT.variantId) return 'RK_BOOT.variantId';
            return 'NONE — drawer will show locked overlay';
          })(),
          isPurchased: isCurrentVariantPurchased()
        };
      }
    }
  };

  /* Auto-init on script load — non-blocking; getBoqItem() will return
     null until onReady fires. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(window);
